/**
 * useViewport — listen to viewport breakpoints via matchMedia.
 *
 * 768px is already the project's mobile breakpoint (referenced in index.css).
 * 1024px gates desktop-wide layouts. Values update on resize/orientation.
 * useMediaQuery answers any other query (a layout with its own breakpoint).
 */

import { useEffect, useMemo, useState } from 'react';

const MOBILE_QUERY = '(max-width: 768px)';
const TABLET_QUERY = '(max-width: 1024px)';

interface ViewportState {
  isMobile: boolean;
  isTablet: boolean;
}

const canMatch = (): boolean => typeof window !== 'undefined' && typeof window.matchMedia === 'function';

/** Whether `query` matches right now. Re-renders only when the answer changes. */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => canMatch() && window.matchMedia(query).matches);

  useEffect(() => {
    if (!canMatch()) return undefined;
    const list = window.matchMedia(query);
    const update = () => setMatches(list.matches);
    // The query may have changed since the first render.
    update();
    list.addEventListener('change', update);
    return () => list.removeEventListener('change', update);
  }, [query]);

  return matches;
}

export function useViewport(): ViewportState {
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const isTablet = useMediaQuery(TABLET_QUERY);
  // Same object until a breakpoint is crossed, so consumers can depend on it.
  return useMemo(() => ({ isMobile, isTablet }), [isMobile, isTablet]);
}
